import { useEffect, useRef, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { AuthedImage } from '@/components/AuthedImage';

type Props = {
  src: string;
  onClose: () => void;
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

/**
 * Moves focus into the container on mount, restores it on unmount, and loops
 * Tab back onto the container's (only) focusable element so it can't escape
 * into the page behind the dialog. The dialog only ever contains its own
 * close button, so there's no multi-element boundary to track here.
 */
function useFocusTrap(containerRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const container = containerRef.current!;
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focusFirst = () => container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)!.focus();
    focusFirst();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      event.preventDefault();
      focusFirst();
    };

    container.addEventListener('keydown', onKeyDown);
    return () => {
      container.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [containerRef]);
}

/**
 * The grid shows square crops, so the whole photo is only visible here: the
 * full image, scaled to fit, over a dimmed page.
 */
export function PhotoLightbox({ src, onClose }: Props) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  useFocusTrap(containerRef);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-label={t('items.photo')}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      {/* Clicking the photo itself should not close the dialog. */}
      <div onClick={(e) => e.stopPropagation()}>
        <AuthedImage src={src} className="max-h-full max-w-full object-contain" />
      </div>
      <button
        type="button"
        aria-label={t('items.closePhoto')}
        className="absolute right-4 top-4 rounded bg-background/80 p-2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        onClick={onClose}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
